import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchProjectDetail,
  fetchProjectSettings,
  saveByokCredentials,
  deleteByokCredentials,
} from '../../lib/api/projects';
import type { ByokFullProvider } from '../../../../shared/types/projects';
import { BYOK_ARN_KEY } from '../../../../shared/types/projects';
import { PLANS, resolvePlan } from '../../../../shared/types/plans';

// ---------------------------------------------------------------------------
// Plan → which providers can be configured on that plan.
// From byok-portal-integration.md:
//   anthropic — Starter+
//   bedrock   — Business+
//   openai    — Enterprise
//   vertex    — Enterprise
//   meta      — Enterprise
// ---------------------------------------------------------------------------

const PLAN_PROVIDERS: Record<string, ByokFullProvider[]> = {
  starter:    ['anthropic'],
  team:       ['anthropic'],
  business:   ['anthropic', 'bedrock'],
  enterprise: ['anthropic', 'bedrock', 'openai', 'vertex', 'meta', 'kimi'],
};

const PROVIDER_LABELS: Record<ByokFullProvider, string> = {
  anthropic: 'Anthropic',
  openai:    'OpenAI',
  bedrock:   'AWS Bedrock',
  vertex:    'Google Vertex AI',
  meta:      'Meta Model API',
  kimi:      'Kimi (Moonshot)',
};

const PROVIDER_ORDER: ByokFullProvider[] = ['anthropic', 'bedrock', 'openai', 'vertex', 'meta', 'kimi'];

// ---------------------------------------------------------------------------
// Top-level tab shell
// ---------------------------------------------------------------------------

export const ByokTab: React.FC<{ projectId: string }> = ({ projectId }) => {
  const settingsQ = useQuery({
    queryKey: ['project', projectId, 'settings'],
    queryFn: () => fetchProjectSettings(projectId),
    enabled: projectId.length > 0,
  });
  const projectQ = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => fetchProjectDetail(projectId),
    enabled: projectId.length > 0,
  });

  if (settingsQ.isLoading || projectQ.isLoading) {
    return <div className="text-sm text-slate-500">Loading…</div>;
  }
  if (settingsQ.isError || projectQ.isError) {
    const msg =
      ((settingsQ.error ?? projectQ.error) as Error | null)?.message ??
      'Failed to load';
    return (
      <div role="alert" className="text-sm text-danger-700">
        {msg}
      </div>
    );
  }
  if (!settingsQ.data || !projectQ.data) return null;

  const plan = resolvePlan({
    plan: projectQ.data.plan,
    tier: projectQ.data.tier,
  });
  const allowedProviders = PLAN_PROVIDERS[plan] ?? ['anthropic'];
  const byok = (settingsQ.data.settings.byok ?? {}) as Record<string, unknown>;

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <h2 className="text-base font-semibold text-slate-900">
          Bring Your Own Keys
        </h2>
        <p className="text-xs text-slate-500 max-w-2xl">
          Forge stores your credentials encrypted in AWS Secrets Manager and injects
          them into every agent task automatically — no config changes required.
          Credentials are write-only: the portal never reads them
          back after saving.
        </p>
      </header>

      <div className="space-y-3">
        {PROVIDER_ORDER.map((provider) => {
          const arnKey = BYOK_ARN_KEY[provider];
          const currentArn = typeof byok[arnKey] === 'string' ? (byok[arnKey] as string) : null;
          const locked = !allowedProviders.includes(provider);
          const lockedPlan = Object.entries(PLAN_PROVIDERS)
            .find(([, ps]) => ps.includes(provider))?.[0] ?? 'business';
          return (
            <ByokProviderCard
              key={provider}
              provider={provider}
              projectId={projectId}
              currentArn={currentArn}
              locked={locked}
              lockedPlan={lockedPlan}
              onMutated={() => {
                void settingsQ.refetch();
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Per-provider card
// ---------------------------------------------------------------------------

const ByokProviderCard: React.FC<{
  provider: ByokFullProvider;
  projectId: string;
  currentArn: string | null;
  locked: boolean;
  lockedPlan: string;
  onMutated: () => void;
}> = ({ provider, projectId, currentArn, locked, lockedPlan, onMutated }) => {
  const [formOpen, setFormOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const configured = currentArn !== null && currentArn.length > 0;

  const handleConfigured = (arn: string) => {
    void arn; // ARN is also stored in settings; we rely on refetch
    setFormOpen(false);
    onMutated();
  };

  const handleRemoved = () => {
    setConfirmRemove(false);
    setFormOpen(false);
    onMutated();
  };

  return (
    <article
      data-testid={`byok-card-${provider}`}
      className={
        'rounded-xl border shadow-sm p-4 bg-white ' +
        (locked ? 'opacity-60' : '')
      }
    >
      {/* Header row */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <ProviderIcon provider={provider} />
          <div>
            <span className="text-sm font-semibold text-slate-900">
              {PROVIDER_LABELS[provider]}
            </span>
            {locked && (
              <span className="ml-2 text-[10px] uppercase tracking-wide font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                {PLANS[lockedPlan as keyof typeof PLANS]?.label ?? lockedPlan}+ required
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <ConfiguredBadge configured={configured} />
          {!locked && (
            <>
              <button
                type="button"
                data-testid={`byok-configure-${provider}`}
                onClick={() => { setFormOpen((o) => !o); setConfirmRemove(false); }}
                className="text-xs px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700
                           hover:bg-slate-50 transition-colors"
              >
                {configured ? (formOpen ? 'Cancel' : 'Update') : (formOpen ? 'Cancel' : 'Configure')}
              </button>
              {configured && !formOpen && (
                <button
                  type="button"
                  data-testid={`byok-remove-${provider}`}
                  onClick={() => { setConfirmRemove((c) => !c); setFormOpen(false); }}
                  className="text-xs px-3 py-1.5 rounded-md border border-danger-200 bg-white text-danger-600
                             hover:bg-danger-50 transition-colors"
                >
                  {confirmRemove ? 'Cancel' : 'Remove'}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Current ARN preview (when configured and not editing) */}
      {configured && !formOpen && !confirmRemove && (
        <p className="mt-2 text-[11px] text-slate-400 font-mono truncate">
          {formatArn(currentArn!)}
        </p>
      )}

      {/* Remove confirm */}
      {confirmRemove && (
        <RemoveConfirm
          provider={provider}
          projectId={projectId}
          onRemoved={handleRemoved}
          onCancel={() => setConfirmRemove(false)}
        />
      )}

      {/* Credential form */}
      {formOpen && (
        <div className="mt-3 border-t pt-3">
          <CredentialForm
            provider={provider}
            projectId={projectId}
            onSaved={handleConfigured}
            onCancel={() => setFormOpen(false)}
          />
        </div>
      )}
    </article>
  );
};

// ---------------------------------------------------------------------------
// Remove confirm row
// ---------------------------------------------------------------------------

const RemoveConfirm: React.FC<{
  provider: ByokFullProvider;
  projectId: string;
  onRemoved: () => void;
  onCancel: () => void;
}> = ({ provider, projectId, onRemoved, onCancel }) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => deleteByokCredentials(projectId, provider),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['project', projectId, 'settings'] });
      onRemoved();
    },
  });

  return (
    <div className="mt-3 border-t pt-3 flex items-center gap-3">
      <p className="text-xs text-slate-600 flex-1">
        Remove the stored {PROVIDER_LABELS[provider]} credential from Secrets Manager?
        Forge will fall back to its default key.
      </p>
      {mutation.isError && (
        <p role="alert" className="text-xs text-danger-600">
          {(mutation.error as Error).message}
        </p>
      )}
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="text-xs px-3 py-1.5 rounded-md bg-danger-600 text-white hover:bg-danger-700
                   disabled:opacity-50 transition-colors"
      >
        {mutation.isPending ? 'Removing…' : 'Confirm remove'}
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={mutation.isPending}
        className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors"
      >
        Cancel
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Credential forms — one per provider
// ---------------------------------------------------------------------------

const CredentialForm: React.FC<{
  provider: ByokFullProvider;
  projectId: string;
  onSaved: (arn: string) => void;
  onCancel: () => void;
}> = ({ provider, projectId, onSaved, onCancel }) => {
  switch (provider) {
    case 'anthropic': return <AnthropicForm projectId={projectId} onSaved={onSaved} onCancel={onCancel} />;
    case 'openai':    return <OpenAiForm    projectId={projectId} onSaved={onSaved} onCancel={onCancel} />;
    case 'bedrock':   return <BedrockForm   projectId={projectId} onSaved={onSaved} onCancel={onCancel} />;
    case 'vertex':    return <VertexForm    projectId={projectId} onSaved={onSaved} onCancel={onCancel} />;
    case 'meta':      return <MetaForm      projectId={projectId} onSaved={onSaved} onCancel={onCancel} />;
    case 'kimi':      return <KimiForm      projectId={projectId} onSaved={onSaved} onCancel={onCancel} />;
  }
};

// Shared save-button + error row
const SaveRow: React.FC<{
  pending: boolean;
  disabled: boolean;
  error: Error | null;
  onSave: () => void;
  onCancel: () => void;
}> = ({ pending, disabled, error, onSave, onCancel }) => (
  <div className="space-y-1">
    {error && (
      <p role="alert" className="text-xs text-danger-600">{error.message}</p>
    )}
    <div className="flex gap-2">
      <button
        type="button"
        onClick={onSave}
        disabled={disabled || pending}
        className="text-xs px-3 py-1.5 rounded-md bg-primary-600 text-white hover:bg-primary-700
                   disabled:opacity-50 transition-colors"
      >
        {pending ? 'Saving…' : 'Save to AWS'}
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={pending}
        className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors"
      >
        Cancel
      </button>
    </div>
    <p className="text-[10px] text-slate-400">
      Saved to AWS Secrets Manager — the raw value never round-trips through this UI again.
    </p>
  </div>
);

// Anthropic
const AnthropicForm: React.FC<{ projectId: string; onSaved: (arn: string) => void; onCancel: () => void }> = ({
  projectId, onSaved, onCancel,
}) => {
  const [apiKey, setApiKey] = useState('');
  const [show, setShow] = useState(false);
  const mutation = useMutation({
    mutationFn: () => saveByokCredentials(projectId, { provider: 'anthropic', credentials: { api_key: apiKey } }),
    onSuccess: (data) => onSaved(data.secret_arn),
  });
  const valid = apiKey.startsWith('sk-ant-');

  return (
    <div className="space-y-3 text-xs">
      <label className="block space-y-1">
        <span className="font-medium text-slate-700">API Key</span>
        <PasswordInput
          value={apiKey}
          show={show}
          onToggle={() => setShow((s) => !s)}
          onChange={setApiKey}
          placeholder="sk-ant-…"
          testId="byok-input-anthropic-api_key"
        />
        <p className="text-[10px] text-slate-400">
          Found at{' '}
          <a href="https://console.anthropic.com/api-keys" target="_blank" rel="noreferrer"
             className="underline hover:text-slate-600">
            console.anthropic.com → API Keys
          </a>
        </p>
        {apiKey && !valid && (
          <p className="text-[10px] text-amber-600">Key must start with <code>sk-ant-</code></p>
        )}
      </label>
      <SaveRow
        pending={mutation.isPending}
        disabled={!valid}
        error={mutation.isError ? (mutation.error as Error) : null}
        onSave={() => mutation.mutate()}
        onCancel={onCancel}
      />
    </div>
  );
};

// OpenAI
const OpenAiForm: React.FC<{ projectId: string; onSaved: (arn: string) => void; onCancel: () => void }> = ({
  projectId, onSaved, onCancel,
}) => {
  const [apiKey, setApiKey] = useState('');
  const [show, setShow] = useState(false);
  const mutation = useMutation({
    mutationFn: () => saveByokCredentials(projectId, { provider: 'openai', credentials: { api_key: apiKey } }),
    onSuccess: (data) => onSaved(data.secret_arn),
  });
  const valid = apiKey.startsWith('sk-');

  return (
    <div className="space-y-3 text-xs">
      <label className="block space-y-1">
        <span className="font-medium text-slate-700">API Key</span>
        <PasswordInput
          value={apiKey}
          show={show}
          onToggle={() => setShow((s) => !s)}
          onChange={setApiKey}
          placeholder="sk-…"
          testId="byok-input-openai-api_key"
        />
        <p className="text-[10px] text-slate-400">
          Found at{' '}
          <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer"
             className="underline hover:text-slate-600">
            platform.openai.com → API Keys
          </a>
        </p>
        {apiKey && !valid && (
          <p className="text-[10px] text-amber-600">Key must start with <code>sk-</code></p>
        )}
      </label>
      <SaveRow
        pending={mutation.isPending}
        disabled={!valid}
        error={mutation.isError ? (mutation.error as Error) : null}
        onSave={() => mutation.mutate()}
        onCancel={onCancel}
      />
    </div>
  );
};

// Bedrock
const BedrockForm: React.FC<{ projectId: string; onSaved: (arn: string) => void; onCancel: () => void }> = ({
  projectId, onSaved, onCancel,
}) => {
  const [keyId, setKeyId] = useState('');
  const [secret, setSecret] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [showSecret, setShowSecret] = useState(false);
  const mutation = useMutation({
    mutationFn: () =>
      saveByokCredentials(projectId, {
        provider: 'bedrock',
        credentials: { access_key_id: keyId, secret_access_key: secret, region },
      }),
    onSuccess: (data) => onSaved(data.secret_arn),
  });
  const valid = keyId.trim().length > 0 && secret.trim().length > 0 && region.trim().length > 0;

  return (
    <div className="space-y-3 text-xs">
      {/* Security warning */}
      <SecurityWarning>
        Use a dedicated IAM user for Forge, not your root account or personal credentials.
        Attach only <code>AmazonBedrockFullAccess</code> (or a tighter custom policy
        scoped to the models you use).
      </SecurityWarning>

      <label className="block space-y-1">
        <span className="font-medium text-slate-700">AWS Access Key ID</span>
        <input
          type="text"
          value={keyId}
          onChange={(e) => setKeyId(e.target.value)}
          placeholder="AKIAIOSFODNN7EXAMPLE"
          data-testid="byok-input-bedrock-access_key_id"
          autoComplete="off"
          className="block w-full border border-slate-300 rounded-md px-3 py-1.5 font-mono bg-white
                     focus:outline-none focus:ring-1 focus:ring-primary-400"
        />
      </label>

      <label className="block space-y-1">
        <span className="font-medium text-slate-700">AWS Secret Access Key</span>
        <PasswordInput
          value={secret}
          show={showSecret}
          onToggle={() => setShowSecret((s) => !s)}
          onChange={setSecret}
          placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
          testId="byok-input-bedrock-secret_access_key"
        />
      </label>

      <label className="block space-y-1">
        <span className="font-medium text-slate-700">AWS Region</span>
        <input
          type="text"
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          placeholder="us-east-1"
          data-testid="byok-input-bedrock-region"
          autoComplete="off"
          className="block w-64 border border-slate-300 rounded-md px-3 py-1.5 font-mono bg-white
                     focus:outline-none focus:ring-1 focus:ring-primary-400"
        />
        <p className="text-[10px] text-slate-400">
          Create an IAM user with <code>AmazonBedrockFullAccess</code> and generate access keys
          in the AWS console under IAM → Users → Security credentials.
        </p>
      </label>

      <SaveRow
        pending={mutation.isPending}
        disabled={!valid}
        error={mutation.isError ? (mutation.error as Error) : null}
        onSave={() => mutation.mutate()}
        onCancel={onCancel}
      />
    </div>
  );
};

// Vertex
const VertexForm: React.FC<{ projectId: string; onSaved: (arn: string) => void; onCancel: () => void }> = ({
  projectId, onSaved, onCancel,
}) => {
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mutation = useMutation({
    mutationFn: () =>
      saveByokCredentials(projectId, {
        provider: 'vertex',
        credentials: { service_account_json: jsonText },
      }),
    onSuccess: (data) => onSaved(data.secret_arn),
  });

  const handleTextChange = (text: string) => {
    setJsonText(text);
    if (!text) { setJsonError(null); return; }
    try {
      JSON.parse(text);
      setJsonError(null);
    } catch (e) {
      setJsonError((e as Error).message);
    }
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      handleTextChange(text);
    };
    reader.readAsText(file);
  };

  const valid = jsonText.trim().length > 0 && !jsonError;

  return (
    <div className="space-y-3 text-xs">
      {/* Security warning */}
      <SecurityWarning>
        The service account should only have the <code>Vertex AI User</code> role — not
        owner or editor. Overly-permissive service accounts pose a data-access risk.
      </SecurityWarning>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="font-medium text-slate-700">Service Account JSON</span>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="text-[10px] border border-slate-200 rounded px-2 py-0.5 hover:bg-slate-50 transition-colors"
          >
            Upload file
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={handleFile}
          />
        </div>
        <textarea
          value={jsonText}
          onChange={(e) => handleTextChange(e.target.value)}
          rows={6}
          data-testid="byok-input-vertex-service_account_json"
          placeholder={'{\n  "type": "service_account",\n  "project_id": "my-project",\n  ...\n}'}
          className="block w-full border border-slate-300 rounded-md px-3 py-2 font-mono text-[11px] bg-white
                     focus:outline-none focus:ring-1 focus:ring-primary-400 resize-y"
        />
        {jsonError && (
          <p className="text-[10px] text-danger-600">Invalid JSON: {jsonError}</p>
        )}
        <p className="text-[10px] text-slate-400">
          Download from{' '}
          <a href="https://console.cloud.google.com/iam-admin/serviceaccounts"
             target="_blank" rel="noreferrer"
             className="underline hover:text-slate-600">
            Google Cloud Console → IAM → Service Accounts → Keys
          </a>.
          Paste the entire JSON file above, or use the Upload button.
        </p>
      </div>

      <SaveRow
        pending={mutation.isPending}
        disabled={!valid}
        error={mutation.isError ? (mutation.error as Error) : null}
        onSave={() => mutation.mutate()}
        onCancel={onCancel}
      />
    </div>
  );
};

// Meta Model API (Muse Wonder / Muse Spark) — OpenAI-compatible; a single API key.
const MetaForm: React.FC<{ projectId: string; onSaved: (arn: string) => void; onCancel: () => void }> = ({
  projectId, onSaved, onCancel,
}) => {
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [show, setShow] = useState(false);
  const mutation = useMutation({
    mutationFn: () =>
      saveByokCredentials(projectId, {
        provider: 'meta',
        credentials: {
          api_key: apiKey,
          // Only send when provided — an empty base URL falls through to the
          // global META_BASE_URL default rather than persisting a blank.
          ...(baseUrl.trim() ? { base_url: baseUrl.trim() } : {}),
        },
      }),
    onSuccess: (data) => onSaved(data.secret_arn),
  });
  // Meta Model API key format is not strictly documented in public preview, so
  // accept any non-empty key rather than enforce a prefix that might reject a
  // valid key. Base URL is optional; when present it must be a valid http(s) URL.
  const baseUrlValid =
    baseUrl.trim().length === 0 || /^https?:\/\/.+/i.test(baseUrl.trim());
  const valid = apiKey.trim().length > 0 && baseUrlValid;

  return (
    <div className="space-y-3 text-xs">
      <label className="block space-y-1">
        <span className="font-medium text-slate-700">API Key</span>
        <PasswordInput
          value={apiKey}
          show={show}
          onToggle={() => setShow((s) => !s)}
          onChange={setApiKey}
          placeholder="Meta Model API key…"
          testId="byok-input-meta-api_key"
        />
        <p className="text-[10px] text-slate-400">
          Create at{' '}
          <a href="https://dev.meta.ai/docs/overview" target="_blank" rel="noreferrer"
             className="underline hover:text-slate-600">
            dev.meta.ai → Meta Model API
          </a>
          . Used for Muse Wonder and Muse Spark models.
        </p>
      </label>
      <label className="block space-y-1">
        <span className="font-medium text-slate-700">
          Base URL <span className="font-normal text-slate-400">(optional)</span>
        </span>
        <input
          type="text"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder="https://api.meta.ai/v1"
          data-testid="byok-input-meta-base_url"
          className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs
                     focus:border-slate-400 focus:outline-none"
        />
        <p className="text-[10px] text-slate-400">
          OpenAI-compatible endpoint. Leave blank to use https://api.meta.ai/v1.
          {baseUrl.trim() && !baseUrlValid ? (
            <span className="ml-1 text-red-500">Must be a valid http(s) URL.</span>
          ) : null}
        </p>
      </label>
      <SaveRow
        pending={mutation.isPending}
        disabled={!valid}
        error={mutation.isError ? (mutation.error as Error) : null}
        onSave={() => mutation.mutate()}
        onCancel={onCancel}
      />
    </div>
  );
};

// Kimi (Moonshot) — OpenAI-compatible; an API key + optional base URL override.
const KimiForm: React.FC<{ projectId: string; onSaved: (arn: string) => void; onCancel: () => void }> = ({
  projectId, onSaved, onCancel,
}) => {
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [show, setShow] = useState(false);
  const mutation = useMutation({
    mutationFn: () =>
      saveByokCredentials(projectId, {
        provider: 'kimi',
        credentials: {
          api_key: apiKey,
          // Only send when provided — blank falls through to the provider
          // default https://api.moonshot.ai/v1 rather than persisting an empty.
          ...(baseUrl.trim() ? { base_url: baseUrl.trim() } : {}),
        },
      }),
    onSuccess: (data) => onSaved(data.secret_arn),
  });
  const baseUrlValid =
    baseUrl.trim().length === 0 || /^https?:\/\/.+/i.test(baseUrl.trim());
  const valid = apiKey.trim().length > 0 && baseUrlValid;

  return (
    <div className="space-y-3 text-xs">
      <label className="block space-y-1">
        <span className="font-medium text-slate-700">API Key</span>
        <PasswordInput
          value={apiKey}
          show={show}
          onToggle={() => setShow((s) => !s)}
          onChange={setApiKey}
          placeholder="Kimi / Moonshot API key…"
          testId="byok-input-kimi-api_key"
        />
        <p className="text-[10px] text-slate-400">
          Create at{' '}
          <a href="https://platform.kimi.ai/console/api-keys" target="_blank" rel="noreferrer"
             className="underline hover:text-slate-600">
            platform.kimi.ai → API Keys
          </a>
          . Used for Kimi K3 models.
        </p>
      </label>
      <label className="block space-y-1">
        <span className="font-medium text-slate-700">
          Base URL <span className="font-normal text-slate-400">(optional)</span>
        </span>
        <input
          type="text"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder="https://api.moonshot.ai/v1"
          data-testid="byok-input-kimi-base_url"
          className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs
                     focus:border-slate-400 focus:outline-none"
        />
        <p className="text-[10px] text-slate-400">
          OpenAI-compatible endpoint. Leave blank to use the default (api.moonshot.ai/v1).
          {baseUrl.trim() && !baseUrlValid ? (
            <span className="ml-1 text-red-500">Must be a valid http(s) URL.</span>
          ) : null}
        </p>
      </label>
      <SaveRow
        pending={mutation.isPending}
        disabled={!valid}
        error={mutation.isError ? (mutation.error as Error) : null}
        onSave={() => mutation.mutate()}
        onCancel={onCancel}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

const ConfiguredBadge: React.FC<{ configured: boolean }> = ({ configured }) => (
  <span
    className={
      'inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full ' +
      (configured
        ? 'bg-success-50 text-success-700 border border-success-200'
        : 'bg-slate-50 text-slate-500 border border-slate-200')
    }
  >
    <span
      aria-hidden
      className={
        'inline-block w-1.5 h-1.5 rounded-full ' +
        (configured ? 'bg-success-500' : 'bg-slate-400')
      }
    />
    {configured ? 'Configured' : 'Not set'}
  </span>
);

const PasswordInput: React.FC<{
  value: string;
  show: boolean;
  onToggle: () => void;
  onChange: (v: string) => void;
  placeholder?: string;
  testId?: string;
}> = ({ value, show, onToggle, onChange, placeholder, testId }) => (
  <div className="flex gap-1">
    <input
      type={show ? 'text' : 'password'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      autoComplete="off"
      className="flex-1 border border-slate-300 rounded-md px-3 py-1.5 text-xs font-mono bg-white
                 focus:outline-none focus:ring-1 focus:ring-primary-400"
    />
    <button
      type="button"
      onClick={onToggle}
      aria-label={show ? 'Hide value' : 'Show value'}
      className="text-[10px] border border-slate-200 rounded-md px-2 bg-white hover:bg-slate-50 transition-colors"
    >
      {show ? 'hide' : 'show'}
    </button>
  </div>
);

const SecurityWarning: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
    <span aria-hidden className="text-amber-500 mt-0.5 shrink-0">⚠</span>
    <p className="text-[11px] text-amber-800">{children}</p>
  </div>
);

const ProviderIcon: React.FC<{ provider: ByokFullProvider }> = ({ provider }) => {
  const base = 'w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold shrink-0';
  const configs: Record<ByokFullProvider, { bg: string; text: string; label: string }> = {
    anthropic: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'A' },
    openai:    { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'AI' },
    bedrock:   { bg: 'bg-orange-100', text: 'text-orange-700', label: 'AWS' },
    vertex:    { bg: 'bg-blue-100', text: 'text-blue-700', label: 'G' },
    meta:      { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'M' },
    kimi:      { bg: 'bg-violet-100', text: 'text-violet-700', label: 'K' },
  };
  const { bg, text, label } = configs[provider];
  return <span className={`${base} ${bg} ${text}`}>{label}</span>;
};

/** Shorten a long ARN for display: show only the last segment. */
function formatArn(arn: string): string {
  const parts = arn.split(':');
  if (parts.length >= 6) {
    const name = parts[parts.length - 1];
    const region = parts[3] ?? '';
    return `arn:…:${region}:…:${name}`;
  }
  return arn;
}

export default ByokTab;
